import React, { useCallback } from 'react';
import { useCanvasAnimation } from './useCanvasAnimation';

interface StructureLayer {
  label: string;
  color: string;
}

export interface StructureSimulationProps {
  size: number;
  layers: StructureLayer[];
}

export function StructureSimulation({ size, layers }: StructureSimulationProps) {
  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, frameCount: number) => {
      ctx.clearRect(0, 0, size, size);
      if (layers.length === 0) return;

      const center = size / 2;
      const maxRadius = size * 0.4;
      const radiusStep = maxRadius / layers.length;
      
      // Animate a slow rotation of the slice opening
      const baseAngle = frameCount * 0.005;
      const sliceAngle = Math.PI / 3; // 60 degree slice removed to see inside
      const startAngle = baseAngle;
      const endAngle = baseAngle + (Math.PI * 2 - sliceAngle);

      for (let i = 0; i < layers.length; i++) {
        // Draw from outside in to ensure outer layers don't cover inner ones
        // Actually, canvas overdraws, so draw outer first, then inner, and slice will show layers?
        // Wait, if we draw outer circle with a slice missing, then the inner circle needs to be fully visible in that slice, but if we draw inner circle AFTER, it covers the center.
        // Actually, if we just draw from outer to inner, with a slice missing from EACH, we see the inside.
        // But wait, the missing slice reveals the inner layers! 
        // So the missing slice of the outer layer should NOT be covered by anything EXCEPT the inner layers.
        
        const layer = layers[i];
        const radius = maxRadius - (i * radiusStep);
        
        // Draw layer body
        ctx.beginPath();
        ctx.moveTo(center, center);
        ctx.arc(center, center, radius, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = layer.color;
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.3)';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw label in the center of its section
        const textAngle = startAngle + (endAngle - startAngle) / 2;
        const textRadius = radius - radiusStep / 2;
        const tx = center + Math.cos(textAngle) * textRadius;
        const ty = center + Math.sin(textAngle) * textRadius;

        ctx.fillStyle = '#ffffff';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Only draw label if it fits, or adjust font
        ctx.fillText(layer.label, tx, ty);
      }
    },
    [size, layers]
  );

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ width: size, height: size }} className="relative flex items-center justify-center">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
